import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, CheckCircle2, Share2, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import LangSwitcher from '@/components/LangSwitcher';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 5 questions calibrées sur la maturité de pilotage de projet en Afrique.
// Chaque option a un score 0–3. Total max = 15.
const QUESTIONS = [
    {
        key: 'volume',
        prompt: 'Combien de projets votre organisation pilote-t-elle actuellement ?',
        options: [
            { label: '1 projet (lancement)', score: 0 },
            { label: '2 à 5 projets', score: 1 },
            { label: '6 à 20 projets', score: 2 },
            { label: 'Plus de 20 projets (portefeuille)', score: 3 },
        ],
    },
    {
        key: 'tooling',
        prompt: 'Comment suivez-vous vos projets aujourd\'hui ?',
        options: [
            { label: 'Excel / Word / emails', score: 0 },
            { label: 'Outils génériques (Trello, Asana, Drive)', score: 1 },
            { label: 'Logiciel projet (MS Project, Smartsheet…)', score: 2 },
            { label: 'Plateforme intégrée métier', score: 3 },
        ],
    },
    {
        key: 'finance',
        prompt: 'Vos dépenses sont-elles rattachées aux budgets ligne par ligne ?',
        options: [
            { label: 'Non, on consolide en fin de mois', score: 0 },
            { label: 'Partiellement, via Excel', score: 1 },
            { label: 'Oui, dans notre comptabilité', score: 2 },
            { label: 'Oui, en temps réel avec validation workflow', score: 3 },
        ],
    },
    {
        key: 'reporting',
        prompt: 'À quelle fréquence générez-vous un reporting bailleur ou COMEX ?',
        options: [
            { label: 'À la demande, en urgence', score: 0 },
            { label: 'Trimestriellement, manuellement', score: 1 },
            { label: 'Mensuellement, semi-automatique', score: 2 },
            { label: 'En 1 clic depuis nos outils', score: 3 },
        ],
    },
    {
        key: 'context',
        prompt: 'Vos équipes opèrent-elles sur le terrain en zones à connectivité limitée ?',
        options: [
            { label: 'Rarement, équipes en siège', score: 0 },
            { label: 'Quelques missions ponctuelles', score: 1 },
            { label: 'Régulièrement, plusieurs pays', score: 2 },
            { label: 'En permanence, terrain rural / multi-sites', score: 3 },
        ],
    },
];

function scoreToProfile(score) {
    if (score <= 4) {
        return {
            tier: 'Démarrage',
            color: 'text-amber-700',
            ring: 'ring-amber-200',
            bg: 'bg-amber-50',
            headline: 'Vous structurez vos premiers projets.',
            body: "Vous gagneriez le plus à formaliser dès maintenant : un workspace par client, un GANTT par projet, un budget rattaché aux dépenses. African Valley propose une offre Explorer gratuite en permanence pour 1 projet et 3 utilisateurs.",
            cta: 'Créer mon espace gratuit',
            link: '/apply',
            recommendation: 'Offre Explorer (gratuite en permanence)',
        };
    }
    if (score <= 9) {
        return {
            tier: 'Croissance',
            color: 'text-secondary',
            ring: 'ring-secondary/30',
            bg: 'bg-secondary-light',
            headline: 'Vous êtes en phase de structuration.',
            body: "Votre organisation gère plusieurs projets en parallèle mais les outils restent fragmentés. African Valley Pro centralise pilotage, finance, GED et reporting bailleurs avec une vue consolidée.",
            cta: "Découvrir l'offre Impact",
            link: '/#tarifs',
            recommendation: 'Offre Impact (3 organisations, 10 projets)',
        };
    }
    if (score <= 12) {
        return {
            tier: 'Portefeuille',
            color: 'text-primary',
            ring: 'ring-primary/30',
            bg: 'bg-primary-light',
            headline: 'Vous pilotez un portefeuille structuré.',
            body: "Vous avez besoin d'industrialiser le reporting, l'audit-trail et le white-label sur l'ensemble de votre portefeuille. African Valley Business offre exports brandés, validations workflow et tableau de bord cross-projets.",
            cta: "Découvrir l'offre Organisation",
            link: '/#tarifs',
            recommendation: 'Offre Organisation (projets illimités, dashboards consolidés)',
        };
    }
    return {
        tier: 'Programme stratégique',
        color: 'text-primary',
        ring: 'ring-primary/40',
        bg: 'bg-gradient-to-br from-primary-light to-secondary-light',
        headline: 'Vous opérez un programme à fort enjeu.',
        body: "Votre maturité projet est avancée et vous cherchez une plateforme prête à intégrer votre SI (API REST), à offrir une souveraineté stricte des données et à gérer plusieurs centaines de projets multi-pays. African Valley Enterprise est conçu pour vous.",
        cta: 'Parler à un architecte',
        link: '/p/contact',
        recommendation: 'Offre Institution (sur devis, API + SLA + Consortium)',
    };
}

const TITLE = 'Diagnostic Maturité Projet 360° — African Valley';
const DESCRIPTION = 'En 5 questions, identifiez votre niveau de maturité de pilotage projet en Afrique et recevez une recommandation personnalisée.';

export default function Diagnostic() {
    const [step, setStep] = useState(0);  // 0..QUESTIONS.length -> form -> result
    const [answers, setAnswers] = useState({});
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [organization, setOrganization] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    // Set OG meta tags for shareable links
    useEffect(() => {
        document.title = TITLE;
        const setMeta = (name, content, isProperty = false) => {
            const attr = isProperty ? 'property' : 'name';
            let el = document.querySelector(`meta[${attr}="${name}"]`);
            if (!el) {
                el = document.createElement('meta');
                el.setAttribute(attr, name);
                document.head.appendChild(el);
            }
            el.setAttribute('content', content);
        };
        setMeta('description', DESCRIPTION);
        setMeta('og:title', TITLE, true);
        setMeta('og:description', DESCRIPTION, true);
        setMeta('og:type', 'website', true);
        setMeta('og:image', `${window.location.origin}/african-valley-hero.jpg`, true);
        setMeta('og:url', `${window.location.origin}/p/diagnostic`, true);
        setMeta('twitter:card', 'summary_large_image');
        setMeta('twitter:title', TITLE);
        setMeta('twitter:description', DESCRIPTION);
    }, []);

    const total = QUESTIONS.length;
    const isQuestion = step < total;
    const isForm = step === total;
    const isResult = step === total + 1;

    const score = Object.values(answers).reduce((s, v) => s + (v?.score || 0), 0);
    const profile = scoreToProfile(score);
    const progressPct = isResult ? 100 : Math.round((step / (total + 1)) * 100);

    const selectOption = (qKey, opt) => {
        setAnswers(prev => ({ ...prev, [qKey]: opt }));
        // auto-advance
        setTimeout(() => setStep(s => s + 1), 250);
    };

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!email || !email.includes('@')) {
            setError('Email invalide');
            return;
        }
        setError('');
        setSubmitting(true);
        try {
            const payload = {
                first_name: (name || '').split(' ')[0] || null,
                last_name: (name || '').split(' ').slice(1).join(' ') || null,
                full_name: name || null,
                email,
                organization: organization || null,
                source: 'diagnostic-quiz',
                message: `Diagnostic Maturité Projet 360° — score ${score}/15 → profil ${profile.tier}. Recommandation : ${profile.recommendation}.`,
            };
            await axios.post(`${API}/access-requests/create`, payload);
            import('@/lib/analytics').then((m) => m.trackEvent('diagnostic_completed', { score, profile: profile.tier }));
            setSubmitted(true);
            setStep(total + 1);
        } catch (e2) {
            setError(e2?.response?.data?.detail || 'Envoi échoué — réessayez');
        } finally {
            setSubmitting(false);
        }
    };

    const skipForm = () => setStep(total + 1);

    const share = async () => {
        const url = `${window.location.origin}/p/diagnostic`;
        const text = `J'ai fait le Diagnostic Maturité Projet 360° d'African Valley — profil ${profile.tier} (${score}/15). Faites le vôtre 👇`;
        if (navigator.share) {
            try {
                await navigator.share({ title: TITLE, text, url });
            } catch { /* user cancelled */ }
        } else {
            try {
                await navigator.clipboard.writeText(`${text} ${url}`);
                toast.success('Lien copié — partagez-le !');
            } catch { /* ignore */ }
        }
    };

    return (
        <div className="min-h-screen flex flex-col relative" data-testid="page-diagnostic">
            {/* Ambient background — official Campus Valley hero with dark overlay */}
            <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none" aria-hidden="true">
                <img src="/brand/campus-hero.jpg" alt=""
                     className="absolute inset-0 w-full h-full object-cover"
                     style={{ filter: 'saturate(1.05)' }}
                     draggable={false} />
                {/* Multi-layer overlay : dark base + subtle warm tint for readability */}
                <div className="absolute inset-0"
                     style={{ background: 'linear-gradient(180deg, rgba(31,26,22,0.85) 0%, rgba(31,26,22,0.92) 60%, rgba(31,26,22,0.96) 100%)' }} />
                <div className="absolute inset-0"
                     style={{ background: 'radial-gradient(circle at 70% 20%, rgba(212,114,47,0.18), transparent 55%)' }} />
            </div>

            <header className="border-b border-white/10 bg-black/30 backdrop-blur-md">
                <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
                    <Link to="/" className="inline-flex items-center gap-3" data-testid="brand-header">
                        <img src="/brand/african-valley-logo-256.png" alt="African Valley · Projets 360°"
                             className="h-10 w-10 object-contain rounded" />
                        <div className="leading-tight">
                            <div className="flex items-baseline gap-1.5">
                                <span className="font-heading text-xl text-white">African</span>
                                <span className="font-heading text-xl text-primary">Valley</span>
                            </div>
                            <span className="text-[9px] uppercase tracking-[0.22em] text-amber-200/70">Diagnostic 360°</span>
                        </div>
                    </Link>
                    <div className="flex items-center gap-4">
                        <LangSwitcher />
                        <Link to="/p/campus" className="text-sm text-white/70 hover:text-white inline-flex items-center gap-1">
                            Campus 360° <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </header>

            <main className="flex-1">
                <div className="max-w-3xl mx-auto px-6 py-12 lg:py-16">
                    {/* Progress bar */}
                    <div className="mb-10">
                        <div className="flex items-center justify-between text-xs uppercase tracking-[0.18em] text-amber-200/80 mb-3">
                            <span>Diagnostic Maturité Projet 360°</span>
                            <span data-testid="diagnostic-progress-label">{progressPct}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-white/15 overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-primary via-amber-400 to-primary transition-all duration-700 ease-out diag-progress"
                                style={{ width: `${Math.max(progressPct, 2)}%`, backgroundSize: '200% 100%' }}
                                data-testid="diagnostic-progress-bar"
                            />
                        </div>
                    </div>

                    {isQuestion && (
                        <div key={step} data-testid={`diagnostic-q-${step}`} className="diag-slide">
                            <div className="flex items-center gap-3 mb-4">
                                {/* Decorative African Valley brand mark per question */}
                                <img
                                    src="/brand/african-valley-logo-96.png"
                                    alt=""
                                    aria-hidden="true"
                                    className="h-9 w-9 rounded ring-1 ring-amber-300/40 shadow-[0_0_18px_rgba(212,114,47,0.35)]"
                                    draggable={false}
                                />
                                <div className="eyebrow text-amber-200 diag-eyebrow tracking-[0.22em]">
                                    Question {step + 1} <span className="text-amber-200/40">/ {total}</span>
                                </div>
                            </div>
                            <h1 className="font-heading text-3xl lg:text-4xl leading-snug mb-8 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.55)]">
                                {QUESTIONS[step].prompt}
                            </h1>
                            <div className="space-y-3">
                                {QUESTIONS[step].options.map((opt, i) => {
                                    const selected = answers[QUESTIONS[step].key]?.label === opt.label;
                                    return (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={() => selectOption(QUESTIONS[step].key, opt)}
                                            data-testid={`diagnostic-option-${step}-${i}`}
                                            style={{ animationDelay: `${100 + i * 70}ms` }}
                                            className={`diag-option w-full text-left p-4 rounded-xl border transition-all flex items-center justify-between gap-3 backdrop-blur-sm ${
                                                selected
                                                    ? 'border-primary bg-primary text-white ring-2 ring-primary/30 shadow-[0_0_28px_rgba(212,114,47,0.5)]'
                                                    : 'border-white/20 bg-white/95 hover:border-primary/60 hover:bg-white hover:translate-x-1 hover:shadow-lg'
                                            }`}
                                        >
                                            <span className={`font-medium ${selected ? 'text-white' : 'text-ink'}`}>{opt.label}</span>
                                            <span className={`shrink-0 h-7 w-7 rounded-full flex items-center justify-center transition-transform ${
                                                selected ? 'bg-white text-primary scale-110' : 'bg-surface-muted text-muted-foreground'
                                            }`}>
                                                <ArrowRight size={14} />
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                            {step > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setStep(s => s - 1)}
                                    className="mt-6 text-xs text-amber-200/70 hover:text-amber-100 inline-flex items-center gap-1 transition-colors"
                                    data-testid="diagnostic-prev"
                                >
                                    <ArrowLeft size={12} /> Précédent
                                </button>
                            )}
                        </div>
                    )}

                    {isForm && (
                        <form onSubmit={submit} className="space-y-6" data-testid="diagnostic-form">
                            <div className="eyebrow text-primary mb-2">Votre diagnostic est prêt</div>
                            <h2 className="font-heading text-3xl lg:text-4xl leading-tight">
                                Recevez le résultat détaillé par email
                            </h2>
                            <p className="text-foreground/70 leading-[1.7]">
                                Nous vous envoyons votre score, votre profil et les ressources adaptées.
                                Aucun engagement, désabonnement en 1 clic.
                            </p>
                            <div className="space-y-3 pt-2">
                                <Input
                                    type="text"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    placeholder="Votre nom complet (facultatif)"
                                    className="h-12"
                                    data-testid="diagnostic-input-name"
                                />
                                <Input
                                    type="text"
                                    value={organization}
                                    onChange={e => setOrganization(e.target.value)}
                                    placeholder="Organisation (facultatif)"
                                    className="h-12"
                                    data-testid="diagnostic-input-org"
                                />
                                <Input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    placeholder="email@organisation.com *"
                                    className="h-12"
                                    data-testid="diagnostic-input-email"
                                />
                            </div>
                            {error && <p className="text-sm text-destructive" data-testid="diagnostic-error">{error}</p>}
                            <div className="flex flex-wrap gap-3 pt-2">
                                <Button
                                    type="submit"
                                    disabled={submitting}
                                    size="lg"
                                    className="bg-primary hover:bg-primary-hover"
                                    data-testid="diagnostic-submit"
                                >
                                    {submitting && <Loader2 size={16} className="mr-2 animate-spin" />}
                                    Recevoir mon diagnostic
                                </Button>
                                <button
                                    type="button"
                                    onClick={skipForm}
                                    className="text-sm text-muted-foreground hover:text-ink self-center"
                                    data-testid="diagnostic-skip"
                                >
                                    Voir sans laisser d'email
                                </button>
                            </div>
                        </form>
                    )}

                    {isResult && (
                        <div className="animate-in fade-in slide-in-from-bottom-3 duration-700" data-testid="diagnostic-result">
                            <div className={`rounded-2xl border-2 ${profile.ring} ${profile.bg} p-8 lg:p-10 ring-2`}>
                                <div className="flex items-start gap-4 mb-6">
                                    <div className={`h-14 w-14 rounded-xl bg-white flex items-center justify-center ${profile.color}`}>
                                        <Sparkles size={26} strokeWidth={1.6} />
                                    </div>
                                    <div className="flex-1">
                                        <div className="eyebrow text-xs text-muted-foreground mb-1">Votre profil</div>
                                        <h2 className={`font-heading text-3xl lg:text-4xl ${profile.color}`} data-testid="diagnostic-tier">
                                            {profile.tier}
                                        </h2>
                                        <div className="text-sm text-muted-foreground mt-1">Score : <strong className="text-ink">{score}/15</strong></div>
                                    </div>
                                </div>
                                <p className="font-heading text-xl mb-4 text-ink">{profile.headline}</p>
                                <p className="text-foreground/75 leading-[1.7] mb-6">{profile.body}</p>
                                <div className="p-4 rounded-lg bg-white/70 border border-border/60 mb-6 flex items-center gap-3">
                                    <CheckCircle2 className="text-secondary shrink-0" size={18} />
                                    <div className="text-sm">
                                        <div className="text-muted-foreground">Notre recommandation</div>
                                        <div className="font-medium text-ink">{profile.recommendation}</div>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-3">
                                    <Link to={profile.link}>
                                        <Button size="lg" className="bg-primary hover:bg-primary-hover" data-testid="diagnostic-cta">
                                            {profile.cta} <ArrowRight size={15} className="ml-2" />
                                        </Button>
                                    </Link>
                                    <button
                                        type="button"
                                        onClick={share}
                                        data-testid="diagnostic-share"
                                        className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-border bg-white hover:bg-surface-muted text-sm font-medium"
                                    >
                                        <Share2 size={14} /> Partager le diagnostic
                                    </button>
                                </div>
                            </div>

                            {submitted && (
                                <div className="mt-6 text-sm text-muted-foreground" data-testid="diagnostic-success-note">
                                    ✓ Votre diagnostic complet vous a été envoyé par email. Notre équipe vous recontactera sous 48 h.
                                </div>
                            )}

                            <div className="mt-10 pt-8 border-t border-border text-center">
                                <p className="text-sm text-muted-foreground mb-4">Envie d'approfondir un secteur en particulier ?</p>
                                <Link to="/p/secteurs">
                                    <Button variant="outline" data-testid="diagnostic-explore-sectors">
                                        Explorer les 14 secteurs <ArrowRight size={14} className="ml-2" />
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
